import React, { useState } from "react";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import { usePostHog } from "posthog-js/react";
import { useDispatch } from "react-redux";
import topicActionCreators from "~/store/topics/actions";
import { Box, Grid, Icon, InputSelect, InputText, InputToggle, Label, Link, Modal, Spinner, Stepper, Text } from "~/components-shared";
import InputTextAreaControl from "~/components/InputTextAreaControl";
import InputTextArea from "~/components-shared/InputTextArea";
import styled, { useTheme } from "styled-components";
import InputDateControl from "~/components/InputDateControl";
import InputSelectControl from "~/components/InputSelectControl";
import lists from "~/lib/lists";
import { RoutesEnum, StatusStateEnum, TopicFootprintSourceEnum, TopicRetroPeriodEnum, TopicTagEnum, TopicVisibilityEnum } from "~/types/enums";
import { useIntegratedSources } from "~/hooks/integrations";
import InputSlackChannelSelectControl from "~/components/InputSlackChannelSelectControl";
import InputJiraProjectSelectControl from "~/components/InputJiraProjectSelectControl";
import InputLinearTeamsSelectControl from "~/components/InputLinearTeamsSelectControl";
import InputAsanaProjectsSelectControl from "~/components/InputAsanaProjectsSelectControl";
import TopicForm from "~/forms/TopicForm";
import InputTextControl from "~/components/InputTextControl";
import InputToggleControl from "~/components/InputToggleControl";
import InputMemberSelectControl from "~/components/InputMemberSelectControl";
import InputGithubRepositorySelectControl from "~/components/InputGithubRepositorySelectControl";
import InputProductboardProductsSelectControl from "~/components/InputProductboardProductsSelectControl";
import api from "~/lib/api";
import InputMondayBoardsSelectControl from "~/components/InputMondayBoardsSelectControl";
import InputConfluenceSpaceSelectControl from "~/components/InputConfluenceSpaceSelectControl";
const Line = styled.div`
  width: 1px;
  background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.borders.primary;
}};
  height: 100%;
`;
const TopicSteppedFlow = _ref2 => {
  let {
    control,
    watch,
    onSubmit,
    trigger,
    status,
    setValue,
    singleColumn
  } = _ref2;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const [activeStep, setActiveStep] = useState(1);
  const [slackChannelDetectionLoading, setSlackChannelDetectionLoading] = useState(false);
  const steps = {
    1: {
      id: 1,
      name: "Description",
      fields: ["description"]
    },
    2: {
      id: 2,
      name: "Keywords",
      fields: ["keywords"]
    },
    3: {
      id: 3,
      name: "Sources",
      fields: ["sources", "settings.slackChannels", "settings.jiraProjects", "settings.confluenceSpaces", "settings.githubRepositories", "settings.mondayBoards", "settings.linearTeams", "settings.asanaProjects", "settings.productboardProducts"]
    },
    4: {
      id: 4,
      name: "Settings",
      fields: ["startDate", "visibility", "ownerId", "memberIds"]
    },
    5: {
      id: 5,
      name: "Title",
      fields: ["name", "markSlackAiDetections", "trigger", "tags"]
    },
    6: {
      id: 6,
      name: "Review"
    }
  };
  const activeSources = useIntegratedSources();
  const sources = Object.keys(lists.topics.footprintSources.names).map(source => ({
    id: source,
    value: lists.topics.footprintSources.names[source],
    hidden: !activeSources.includes(source)
  }));
  const onCancel = () => {
    posthog.capture(`Topics Create – Cancel`);
    dispatch(topicActionCreators.actionChanged(null, null));
  };
  const handleSlackChannelDetection = () => {
    setSlackChannelDetectionLoading(true);
    api.detectSlackTopicChannels({
      topicName: watch("description"),
      topicDescription: watch("description")
    }).then(response => {
      setValue("settings.slackChannels", response.map(channel => channel.id));
    }).finally(() => {
      setSlackChannelDetectionLoading(false);
    });
  };
  const onContinue = async () => {
    if (activeStep === 6) {
      posthog.capture(`Topics Create – Save Topic`);
      onSubmit();
    } else {
      posthog.capture(`Topics Create – ${steps[activeStep].name} Continue`, Object.fromEntries(steps[activeStep].fields.map(field => [field, watch(field)])));
      const valid = await trigger(steps[activeStep].fields);
      if (valid) {
        setActiveStep(prev => prev + 1);
      }
    }
  };
  const onBack = () => {
    posthog.capture(`Topics Create – Go Back`);
    setActiveStep(prev => prev - 1);
  };
  const view = index => {
    switch (index) {
      case 1:
        return /*#__PURE__*/React.createElement(Grid, {
          cols: 3,
          gap: 10,
          template: "1fr 1px 1fr",
          s: {
            pt: 5,
            px: 5
          },
          fullHeight: true
        }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
          element: "h3",
          weight: "semiBold",
          s: {
            mb: 1
          }
        }, "Topic Description"), /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "To get started, can you tell us a little bit about an initiative or cross-functional deliverable that matters to you right now"), /*#__PURE__*/React.createElement(InputTextAreaControl, {
          name: "description",
          rows: 7,
          control: control
        })), /*#__PURE__*/React.createElement(Line, null), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
          color: colors.primary.main,
          s: {
            mb: 1
          },
          element: "h3"
        }, "Real-Life Example"), /*#__PURE__*/React.createElement(Text, {
          color: "textSecondary",
          display: "block",
          weight: "medium",
          fontSize: 4,
          lineHeight: 6,
          s: {
            mb: 3
          }
        }, "To get started, can you tell us a little bit about an initiative or cross-functional deliverable that matters to you right now"), /*#__PURE__*/React.createElement(InputTextArea, {
          value: "We are looking for ways to improve our Bug and Feature request intake process to better filter, categorize, and coordinate our customer requests in collaboration with Customer Success and Engineering teams. This includes the process of capturing customer feedback and requests, categorizing based on request type, and filtering based on priority and need before creating a ticket for engineering to add to their backing or current sprint.",
          rows: 7,
          disabled: true
        })));
      case 2:
        return /*#__PURE__*/React.createElement(Grid, {
          cols: 3,
          gap: 10,
          template: "1fr 1px 1fr",
          s: {
            pt: 5,
            px: 5
          },
          fullHeight: true
        }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
          element: "h3",
          weight: "semiBold",
          s: {
            mb: 1
          }
        }, "Topic Keywords"), /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "If you had to pull out a set of key words that you would use to search for updates connected to this topic, what would they be? (comma-separated)"), /*#__PURE__*/React.createElement(InputTextControl, {
          name: "keywords",
          control: control
        })), /*#__PURE__*/React.createElement(Line, null), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
          color: colors.primary.main,
          s: {
            mb: 1
          },
          element: "h3"
        }, "Real-Life Example"), /*#__PURE__*/React.createElement(Text, {
          color: "textSecondary",
          display: "block",
          weight: "medium",
          fontSize: 4,
          lineHeight: 6,
          s: {
            mb: 3
          }
        }, "If you had to pull out a set of key words that you would use to search for updates connected to this topic, what would they be? (comma-separated)"), /*#__PURE__*/React.createElement(InputText, {
          value: "Customer Feedback, Customer Bug Request, CS Feedback",
          disabled: true
        })));
      case 3:
        return /*#__PURE__*/React.createElement(Grid, {
          cols: 3,
          gap: 10,
          template: "1fr 1px 1fr",
          s: {
            pt: 5,
            px: 5
          },
          fullHeight: true
        }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
          element: "h3",
          weight: "semiBold",
          s: {
            mb: 1
          }
        }, "Topic Sources"), /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "Where is this work happening?"), /*#__PURE__*/React.createElement(InputSelectControl, {
          name: "sources",
          options: sources,
          s: {
            mb: 5
          },
          control: control,
          placeholder: "Select Detection Sources...",
          noOptionsText: `There are **no** available Sources available.\n\n Please follow the [Integrations](${RoutesEnum.INTEGRATIONS}) section\n\n to enable integrations.`,
          fullSelections: true,
          multiple: true
        }), watch("sources").includes(TopicFootprintSourceEnum.SLACK) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InputSlackChannelSelectControl, {
          label: t("TopicForm.slackChannels"),
          name: "settings.slackChannels",
          placeholder: "Select Channels...",
          s: {
            mb: 2
          },
          multiple: true,
          control: control,
          fullSelections: true,
          showPrivateHint: true,
          required: true
        }), /*#__PURE__*/React.createElement(Box, {
          justify: "end",
          s: {
            mb: 5
          }
        }, /*#__PURE__*/React.createElement(Link, {
          onClick: handleSlackChannelDetection,
          color: "textSecondary",
          underline: true
        }, slackChannelDetectionLoading ? /*#__PURE__*/React.createElement(Spinner, {
          scale: 3.5,
          s: {
            mr: 1
          }
        }) : /*#__PURE__*/React.createElement(Icon, {
          src: "sparkles",
          s: {
            mr: 1
          }
        }), t("TopicForm.slackAutoDetectChannels")))), watch("sources").includes(TopicFootprintSourceEnum.JIRA) && /*#__PURE__*/React.createElement(InputJiraProjectSelectControl, {
          label: t("TopicForm.jiraProjects"),
          name: "settings.jiraProjects",
          placeholder: "Select Projects...",
          s: {
            mb: 5
          },
          multiple: true,
          control: control,
          fullSelections: true,
          required: true
        }), watch("sources").includes(TopicFootprintSourceEnum.CONFLUENCE) && /*#__PURE__*/React.createElement(InputConfluenceSpaceSelectControl, {
          label: t("TopicForm.confluenceSpaces"),
          name: "settings.confluenceSpaces",
          placeholder: "Select Spaces...",
          s: {
            mb: 5
          },
          multiple: true,
          control: control,
          fullSelections: true,
          required: true
        }), watch("sources").includes(TopicFootprintSourceEnum.LINEAR) && /*#__PURE__*/React.createElement(InputLinearTeamsSelectControl, {
          label: t("TopicForm.linearTeams"),
          name: "settings.linearTeams",
          placeholder: "Select Teams...",
          s: {
            mb: 5
          },
          multiple: true,
          control: control,
          fullSelections: true,
          required: true
        }), watch("sources").includes(TopicFootprintSourceEnum.ASANA) && /*#__PURE__*/React.createElement(InputAsanaProjectsSelectControl, {
          label: t("TopicForm.asanaProjects"),
          name: "settings.asanaProjects",
          placeholder: "Select Projects...",
          s: {
            mb: 5
          },
          multiple: true,
          control: control,
          fullSelections: true,
          required: true
        }), watch("sources").includes(TopicFootprintSourceEnum.GITHUB) && /*#__PURE__*/React.createElement(InputGithubRepositorySelectControl, {
          label: t("TopicForm.githubRepositories"),
          name: "settings.githubRepositories",
          placeholder: "Select Repositories...",
          s: {
            mb: 5
          },
          multiple: true,
          control: control,
          fullSelections: true,
          required: true
        }), watch("sources").includes(TopicFootprintSourceEnum.PRODUCTBOARD) && /*#__PURE__*/React.createElement(InputProductboardProductsSelectControl, {
          label: t("TopicForm.productboardProducts"),
          name: "settings.productboardProducts",
          placeholder: "Select Products...",
          s: {
            mb: 5
          },
          multiple: true,
          control: control,
          fullSelections: true,
          required: true
        }), watch("sources").includes(TopicFootprintSourceEnum.MONDAY) && /*#__PURE__*/React.createElement(InputMondayBoardsSelectControl, {
          label: t("TopicForm.mondayBoards"),
          name: "settings.mondayBoards",
          placeholder: "Select Boards...",
          s: {
            mb: 5
          },
          multiple: true,
          control: control,
          fullSelections: true,
          required: true
        }), !!watch("sources").length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "Do you want us to look back and capture any of the previous work that was done prior to today?"), Object.keys(lists.topics.retroPeriod.names).map(id => /*#__PURE__*/React.createElement(Button, {
          key: id,
          elemBefore: /*#__PURE__*/React.createElement(Icon, {
            src: id === TopicRetroPeriodEnum.NOT_SET ? "cross" : "timelapse",
            s: {
              mr: 1,
              mt: -0.5
            }
          }),
          color: watch("retroPeriod") === id ? "primary" : "grey",
          display: "inline-block",
          fontSize: 3,
          onClick: () => setValue("retroPeriod", id),
          outline: false,
          s: {
            pl: 2,
            pr: 3,
            py: 1,
            mr: 2,
            mb: 2
          }
        }, lists.topics.retroPeriod.names[id])))));
      case 4:
        return /*#__PURE__*/React.createElement(Grid, {
          cols: 3,
          gap: 10,
          template: "1fr 1px 1fr",
          s: {
            pt: 5,
            px: 5
          },
          fullHeight: true
        }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
          element: "h3",
          weight: "semiBold",
          s: {
            mb: 1
          }
        }, "Topic Settings"), /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "When did you or the team start working on this?"), /*#__PURE__*/React.createElement(InputDateControl, {
          name: "startDate",
          control: control,
          s: {
            mb: 8
          },
          maxWidth: "240px"
        }), /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "Who should have access to this topic? Access meaning they can see, query, and review progress"), /*#__PURE__*/React.createElement(InputSelectControl, {
          name: "visibility",
          options: Object.keys(lists.topics.visibility.names).map(key => ({
            id: key,
            value: lists.topics.visibility.names[key],
            tip: lists.topics.visibility.descriptions[key]
          })),
          control: control,
          required: true,
          maxWidth: "200px",
          s: {
            mb: 3
          }
        }), watch("visibility") !== TopicVisibilityEnum.PRIVATE && /*#__PURE__*/React.createElement(Grid, {
          cols: 2,
          gap: 3,
          template: "4fr 6fr"
        }, /*#__PURE__*/React.createElement(InputMemberSelectControl, {
          name: "ownerId",
          label: t("TopicForm.owner"),
          placeholder: "Select Member...",
          control: control,
          required: true
        }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Label, {
          s: {
            mb: 2
          }
        }, t("TopicForm.stakeholders")), /*#__PURE__*/React.createElement(Icon, {
          s: {
            ml: 0.5,
            mt: -0.5
          },
          color: "secondary",
          "data-tooltip-id": "general-tooltip",
          "data-tooltip-html": "<strong>Stakeholder</strong> members with permission to view topic progress and updates.",
          src: "infoOutline",
          style: {
            cursor: "help"
          }
        }), /*#__PURE__*/React.createElement(InputMemberSelectControl, {
          name: "memberIds",
          filterMembers: [watch("ownerId")],
          control: control,
          placeholder: "Select Members...",
          s: {
            mb: 3
          },
          fullSelections: true,
          multiple: true
        })))));
      case 5:
        return /*#__PURE__*/React.createElement(Grid, {
          cols: 3,
          gap: 10,
          template: "1fr 1px 1fr",
          s: {
            pt: 5,
            px: 5
          },
          fullHeight: true
        }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
          element: "h3",
          weight: "semiBold",
          s: {
            mb: 1
          }
        }, "Identify Your Topic"), /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "What would you like this topic to be called in Joyn?"), /*#__PURE__*/React.createElement(InputTextControl, {
          name: "name",
          control: control,
          s: {
            mb: 5
          },
          required: true
        }), /*#__PURE__*/React.createElement(InputSelectControl, {
          name: "tags",
          s: {
            mb: 5
          },
          label: t("TopicForm.tags"),
          options: Object.keys(lists.topics.tags.names).map(key => ({
            id: key,
            value: lists.topics.tags.names[key],
            accessory: [TopicTagEnum.BUG, TopicTagEnum.FEATURE, TopicTagEnum.KTLO].includes(key) ? /*#__PURE__*/React.createElement(Text, {
              display: "block",
              color: "textSecondary",
              weight: "semiBold",
              fontSize: 3
            }, "Mutually Exclusive") : undefined
          })),
          control: control,
          mutuallyExclusive: [TopicTagEnum.BUG, TopicTagEnum.FEATURE, TopicTagEnum.KTLO],
          multiple: true,
          fullSelections: true,
          required: true
        }), watch("sources").includes(TopicFootprintSourceEnum.SLACK) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "Do you want us to indicate in Slack when a footprint has been mapped? We do this by adding a \u2611\uFE0F to the message"), /*#__PURE__*/React.createElement(InputToggleControl, {
          name: "markSlackAiDetections",
          label: t("TopicForm.markSlackAiDetections"),
          control: control,
          display: "block",
          s: {
            mb: 5
          }
        })), /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "When a relevant message, ticket, piece of content, and/or event is not mapped, what unique tag would you like to use to make sure it maps to this specific topic?"), /*#__PURE__*/React.createElement(InputTextControl, {
          elemBefore: /*#__PURE__*/React.createElement(Text, {
            fontSize: 4,
            weight: "light"
          }, "#"),
          name: "trigger",
          prefix: "joyn_",
          s: {
            mb: 5
          },
          control: control,
          maxWidth: "200px",
          required: true
        })), /*#__PURE__*/React.createElement(Line, null), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
          color: colors.primary.main,
          s: {
            mb: 1
          },
          element: "h3"
        }, "Real-Life Example"), /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          color: "textSecondary",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "What would you like this topic to be called in Joyn?"), /*#__PURE__*/React.createElement(InputText, {
          value: "Customer Bugs and Feedback Flow",
          s: {
            mb: 5
          },
          required: true,
          disabled: true
        }), /*#__PURE__*/React.createElement(InputSelect, {
          s: {
            mb: 5
          },
          label: t("TopicForm.tags"),
          value: [TopicTagEnum.BUG, TopicTagEnum.CUSTOMER],
          options: Object.keys(lists.topics.tags.names).map(key => ({
            id: key,
            value: lists.topics.tags.names[key],
            accessory: [TopicTagEnum.BUG, TopicTagEnum.FEATURE, TopicTagEnum.KTLO].includes(key) ? /*#__PURE__*/React.createElement(Text, {
              display: "block",
              color: "textSecondary",
              weight: "semiBold",
              fontSize: 3
            }, "Mutually Exclusive") : undefined
          })),
          multiple: true,
          fullSelections: true,
          required: true,
          disabled: true
        }), watch("sources").includes(TopicFootprintSourceEnum.SLACK) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          color: "textSecondary",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "Do you want us to indicate in Slack when a footprint has been mapped? We do this by adding a \u2611\uFE0F to the message"), /*#__PURE__*/React.createElement(InputToggle, {
          name: "markSlackAiDetections",
          label: t("TopicForm.markSlackAiDetections"),
          value: true,
          display: "block",
          s: {
            mb: 5
          }
        })), /*#__PURE__*/React.createElement(Text, {
          fontSize: 4,
          lineHeight: 6,
          display: "block",
          color: "textSecondary",
          weight: "medium",
          s: {
            mb: 3
          }
        }, "When a relevant message, ticket, piece of content, and/or event is not mapped, what unique tag would you like to use to make sure it maps to this specific Topic?"), /*#__PURE__*/React.createElement(InputText, {
          elemBefore: /*#__PURE__*/React.createElement(Text, {
            fontSize: 4,
            weight: "light"
          }, "#"),
          value: "joyn_customer",
          s: {
            mb: 5
          },
          maxWidth: "200px",
          required: true,
          disabled: true
        })));
      case 6:
        return /*#__PURE__*/React.createElement(TopicForm, {
          watch: watch,
          control: control,
          trigger: trigger,
          singleColumn: singleColumn,
          setValue: setValue
        });
      default:
        return null;
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Modal.Content, null, view(activeStep)), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "400px 1fr",
    fullWidth: true,
    align: "center"
  }, /*#__PURE__*/React.createElement(Stepper, {
    maxWidth: "400px",
    activeValue: activeStep,
    data: Object.values(steps)
  }), /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "center"
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    template: activeStep > 1 ? "auto auto 230px" : "auto 230px",
    justify: "end",
    gap: 3
  }, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    display: "inline-block",
    s: {
      px: 8
    },
    onClick: onCancel,
    rounded: true
  }, t("common.actions.cancel")), activeStep > 1 && /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    display: "inline-block",
    s: {
      px: 8
    },
    onClick: onBack,
    rounded: true
  }, t("common.actions.back")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    display: "inline-block",
    type: "button",
    maxWidth: "230px",
    onClick: onContinue,
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, {
    5: t("common.actions.review"),
    6: t("TopicCreateModal.saveTopic")
  }[activeStep] || t("common.actions.continue")))))));
};
export default TopicSteppedFlow;